import React from "react";
import { useNavigate } from "react-router-dom";
import { FeatherArrowLeft } from "@subframe/core";
import { SettingsNav } from "../components/SettingsNav";
import { WORKBENCH_PATH } from "../config/settingsNavigation";

interface SettingsLayoutProps {
  children: React.ReactNode;
  /**
   * Let the panel use the full window width.
   *
   * Forms read better in a measured column, so that is the default. Tables do
   * not: the chart of accounts is six columns plus actions, and squeezing it
   * into the form width costs the account names their room for no gain.
   */
  wide?: boolean;
}

/**
 * Full-screen chrome for the settings area: panel rail on the left, the
 * selected panel on the right.
 *
 * The app sidebar is deliberately absent. Settings is somewhere you go to
 * change how the workspace behaves and then leave, not somewhere you work from,
 * and two stacked navigation columns would compete for the same job. The rail
 * carries the one way back instead.
 */
export const SettingsLayout: React.FC<SettingsLayoutProps> = ({ children, wide = false }) => {
  const navigate = useNavigate();

  return (
    <div className="flex h-screen w-full items-start bg-default-background">
      <div className="flex w-64 shrink-0 flex-col gap-6 self-stretch overflow-y-auto border-r border-solid border-neutral-border px-4 py-6">
        <button
          type="button"
          onClick={() => navigate(WORKBENCH_PATH)}
          className="flex items-center gap-2 self-start rounded-md px-3 py-1.5 text-body font-body text-subtext-color transition-colors hover:bg-neutral-50 hover:text-default-font"
        >
          <FeatherArrowLeft className="text-body font-body" />
          Back to workbench
        </button>

        <div className="flex flex-col gap-4">
          <span className="px-3 text-heading-3 font-heading-3 text-default-font">Settings</span>
          <SettingsNav />
        </div>
      </div>

      <div className="flex h-full grow shrink-0 basis-0 flex-col items-start overflow-y-auto px-8">
        <div
          className={`flex w-full grow shrink-0 basis-0 flex-col items-start py-8 ${
            wide ? "" : "lg:max-w-4xl"
          }`}
        >
          {children}
        </div>
      </div>
    </div>
  );
};
